import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Collapse,
  alpha,
  useTheme
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SummarizeIcon from '@mui/icons-material/Summarize';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const LineProductionAssemblyPermissions = ({ 
  permissions, 
  roleForm, 
  setRoleForm 
}) => {
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = useState({
    line_assembly_overview: false,
    line_assembly_items: false,
    line_assembly_cost: false,
    line_assembly_summary: false
  });
  
  // Line assembly pages mapping
  const lineAssemblyPages = [
    'line_assembly_overview', 
    'line_assembly_items', 
    'line_assembly_cost', 
    'line_assembly_summary'
  ];
  
  const pageNameMapping = {
    'line_assembly_overview': 'Line Overview Page',
    'line_assembly_items': 'Line Items Page',
    'line_assembly_cost': 'Line Cost Page',
    'line_assembly_summary': 'Line Summary Page'
  };
  
  const pageIconMapping = {
    'line_assembly_overview': <DashboardIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'line_assembly_items': <InventoryIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'line_assembly_cost': <AttachMoneyIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'line_assembly_summary': <SummarizeIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
  };

  const renderLineAssemblyPagePermissions = (pageKey, pageName) => {
    const isExpanded = expandedSections[pageKey] !== false;
    let pagePermissions = [];
    
    if (pageKey === 'line_assembly_overview') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.assembly.overview'));
    } else if (pageKey === 'line_assembly_items') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.assembly.items'));
    } else if (pageKey === 'line_assembly_cost') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.assembly.cost'));
    } else if (pageKey === 'line_assembly_summary') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.assembly.summary'));
    }

    if (pagePermissions.length === 0) return null;

    const viewPerm = pagePermissions.find(p => p.name?.includes('.view'));
    const editPerm = pagePermissions.find(p => p.name?.includes('.edit'));
    const deletePerm = pagePermissions.find(p => p.name?.includes('.delete'));
    const approvePerm = permissions.find(p => p.name === 'production.line.assembly.summary.approve');

    return (
      <Box 
        key={pageKey}
        sx={{ 
          mb: 2,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
          borderRadius: 2,
          overflow: 'hidden',
          background: 'white'
        }}
      >
        {/* Page Header */}
        <Box
          onClick={() => setExpandedSections(prev => ({ ...prev, [pageKey]: !isExpanded }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.grey[400], 0.3)}` : 'none',
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.03)})`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {pageIconMapping[pageKey]}
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {pageName}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isExpanded ? '▼' : '▶'}
          </Typography>
        </Box>

        {/* Permission Switches */}
        <Collapse in={isExpanded}>
          <Box sx={{ p: 2 }}>
            {/* View Permission */}
            {viewPerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: (editPerm || deletePerm || approvePerm) ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    View
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can view {pageName.toLowerCase()}
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[viewPerm.id]?.read || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[viewPerm.id]) {
                      newPermissions[viewPerm.id] = {};
                    }
                    newPermissions[viewPerm.id].read = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Edit Permission - for line_assembly_overview, line_assembly_items, line_assembly_cost */}
            {editPerm && (pageKey === 'line_assembly_overview' || pageKey === 'line_assembly_items' || pageKey === 'line_assembly_cost') && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: (pageKey === 'line_assembly_items' && deletePerm) || (pageKey === 'line_assembly_summary' && approvePerm) ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Edit
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can edit {pageName.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[editPerm.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[editPerm.id]) {
                      newPermissions[editPerm.id] = {};
                    }
                    newPermissions[editPerm.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Delete Permission - only for line_assembly_items */}
            {deletePerm && pageKey === 'line_assembly_items' && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: (pageKey === 'line_assembly_summary' && approvePerm) ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Delete
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can delete {pageName.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[deletePerm.id]?.delete || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[deletePerm.id]) {
                      newPermissions[deletePerm.id] = {};
                    }
                    newPermissions[deletePerm.id].delete = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Approve Production Permission - only for line_assembly_summary */}
            {approvePerm && pageKey === 'line_assembly_summary' && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Approve Production
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can approve production lines
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[approvePerm.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[approvePerm.id]) {
                      newPermissions[approvePerm.id] = {};
                    }
                    newPermissions[approvePerm.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      {lineAssemblyPages.map(pageKey => {
        const pageName = pageNameMapping[pageKey] || pageKey;
        return renderLineAssemblyPagePermissions(pageKey, pageName);
      })}
    </Box>
  );
};

export default LineProductionAssemblyPermissions;
